/**
 * « Mes premiers pas » (onboarding du nouveau commercial — L8).
 * Chemin guidé 3 étapes affiché en tête de home tant qu'il n'est pas terminé.
 * Complétion = source de vérité SERVEUR (GET /api/users/me/first-steps), qui
 * déclenche aussi l'auto-instanciation du parcours d'évaluation (lazy) :
 *   s1 = évaluation terminée · s3 = prépa RDV · s4 = débrief/enregistrement RDV
 *   (s2 « programme posé » retirée avec « Mon programme » le 25/09/2026).
 * Chaque étape est un lien profond. Masquable (localStorage).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { firstStepsApi, type FirstStepsState } from "../lib/api";

const DISMISS_KEY = "bizcoach_first_steps_dismissed";

export function FirstStepsCard({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { t } = useTranslation();
  const [state, setState] = useState<FirstStepsState | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    firstStepsApi.getMine().then(setState).catch(() => setState(null));
  }, []);

  if (dismissed || !state) return null;
  const allDone = state.s1 && state.s3 && state.s4;
  if (allDone) return null;

  const steps = [
    { done: state.s1, icon: "📊", title: t("firstSteps.s1"), nav: "certification-paths", coins: 50 },
    { done: state.s3, icon: "🎙", title: t("firstSteps.s3"), nav: "sessions-live", coins: 20 },
    { done: state.s4, icon: "🎧", title: t("firstSteps.s4"), nav: "sales-coach-new", coins: 30 },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  const dismiss = () => { try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ } setDismissed(true); };

  // Deep-link : cibler LE parcours d'évaluation précis (surligné/scrollé côté page Parcours).
  const go = (nav: string) => {
    if (nav === "certification-paths" && state.evaluationPathId) {
      try { localStorage.setItem("bizcoach_focus_path_id", state.evaluationPathId); } catch { /* noop */ }
    }
    onNavigate?.(nav);
  };

  return (
    <div className="rounded-2xl border p-5 shadow-[var(--elev-2)]" style={{ borderColor: "#988EF2", background: "linear-gradient(120deg,#EDE9FB,#E7F0FA)" }}>
      <div className="mb-1 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
          👋 {t("firstSteps.title")}
          <span className="rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: "#988EF2", color: "white" }}>{doneCount}/{steps.length}</span>
        </h2>
        <button onClick={dismiss} className="-my-1 -mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-white/60" title={t("common.close")} aria-label={t("common.close")}>
          <X className="h-4 w-4" />
        </button>
      </div>
      {/* Le rappel de contexte ne sert qu'à la découverte : sur mobile il
          coûte une ligne du premier écran à chaque visite. */}
      <p className="mb-3 hidden text-sm text-muted-foreground sm:block">{t("firstSteps.subtitle")}</p>
      <div className="flex flex-col gap-2">
        {steps.map((s, i) => (
          <button
            key={i}
            onClick={() => go(s.nav)}
            className={`flex items-center gap-3 rounded-xl border bg-white/80 px-3 py-2.5 text-left transition hover:bg-white ${
              s.done ? "hidden sm:flex" : ""
            }`}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm text-white" style={{ background: s.done ? "#1E7A3C" : "#988EF2" }}>
              {s.done ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <span className="flex-1 text-sm font-medium" style={{ color: "#384872", textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.6 : 1 }}>{s.icon} {s.title}</span>
            {!s.done && <span className="rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: "#FBF3DA", color: "#926B00" }}>+{s.coins} 🪙</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
